import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ArrowLeft, Link2, Loader2, FilePlus2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

/**
 * BankingMatch — full-page version of the "Find Matching Entry" Banking dialog.
 * Route: /banking/transactions/:transactionId/match
 */
export default function BankingMatch() {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;

  const [transaction, setTransaction] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkingEntry, setLinkingEntry] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!wsId || !transactionId) return;
    setLoading(true);
    try {
      const [txnRes, matchRes] = await Promise.all([
        apiFetch(`${API_URL}/api/banking/transactions/${transactionId}?workspace_id=${wsId}`),
        apiFetch(`${API_URL}/api/banking/transactions/${transactionId}/match-suggestions?workspace_id=${wsId}`),
      ]);
      if (txnRes.ok) setTransaction(await txnRes.json());
      if (matchRes.ok) setSuggestions(await matchRes.json());
    } finally { setLoading(false); }
  }, [wsId, transactionId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const linkEntry = async (entryId) => {
    if (!transaction) return;
    setLinkingEntry(entryId);
    try {
      const r = await apiFetch(`${API_URL}/api/banking/transactions/${transaction.transaction_id}/link?workspace_id=${wsId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry_id: entryId }),
      });
      if (r.ok) {
        toast.success('Transaction linked to entry');
        navigate('/banking');
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to link entry');
      }
    } finally { setLinkingEntry(null); }
  };

  return (
    <div className="container mx-auto p-6 max-w-3xl space-y-5" data-testid="banking-match-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/banking')} data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Banking
      </Button>

      <div>
        <div className="flex items-center gap-2">
          <Link2 className="w-6 h-6 text-emerald-500" />
          <h1 className="text-2xl font-semibold tracking-tight">Match transaction to journal entry</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Link this bank transaction to an existing journal entry, or create a new entry from scratch.
        </p>
      </div>

      {/* Transaction Summary */}
      <Card>
        <CardContent className="p-4">
          {loading && !transaction ? (
            <Skeleton className="h-16" />
          ) : !transaction ? (
            <p className="text-sm text-muted-foreground italic">Transaction not found.</p>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{transaction.merchant_name || transaction.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{formatDate(transaction.date)} · {transaction.account_name || transaction.bank_id}</p>
              </div>
              <p className="font-mono font-semibold text-xl">{formatCurrency(transaction.amount)}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Suggestions */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <h2 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Suggested matches ({suggestions.length})</h2>
          {loading ? (
            <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-8 space-y-3">
              <p className="text-sm text-muted-foreground">No matching journal entries found.</p>
              <Button onClick={() => navigate(`/journal-entries/new?txn=${transactionId}`)} data-testid="create-new-entry-btn">
                <FilePlus2 className="w-4 h-4 mr-1.5" /> Create new entry from this transaction
              </Button>
            </div>
          ) : (
            <div className="space-y-2" data-testid="match-suggestions">
              {suggestions.map(sugg => (
                <div key={sugg.entry_id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/30" data-testid={`suggestion-${sugg.entry_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-medium">JE #{sugg.entry_no}</span>
                      <Badge variant="outline" className="text-[10px]">{Math.round(sugg.score * 100)}% match</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{sugg.memo || 'No memo'}</p>
                  </div>
                  <div className="flex items-center gap-4 ml-3">
                    <div className="text-right">
                      <p className="font-mono font-medium text-sm">{formatCurrency(sugg.total_amount)}</p>
                      <p className="text-[10px] text-muted-foreground">{formatDate(sugg.txn_date)}</p>
                    </div>
                    <Button size="sm" onClick={() => linkEntry(sugg.entry_id)} disabled={linkingEntry === sugg.entry_id} data-testid={`link-${sugg.entry_id}`}>
                      {linkingEntry === sugg.entry_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Link'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
